import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Eye, FileText, Send, MessageCircle, Mail, Share, Upload, AlertCircle, X, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ViewInvoice = ({ invoice, onClose }) => {
  const { user } = useAuth();
  const [isSharing, setIsSharing] = useState(false);
  const [showWhatsappImageModal, setShowWhatsappImageModal] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSendingImage, setIsSendingImage] = useState(false);

  const calculateGSTBreakdown = (invoice, user) => {
    const baseAmount = invoice.base_amount || invoice.amount || 0;
    const gstRate = invoice.gst_rate || 0;
    
    if (user?.gstin && invoice.client_gst_enabled && gstRate > 0) {
      const userGstinPrefix = user?.gstin?.substring(0, 2) || '';
      const clientGstin = invoice.client_gstin || invoice.client?.gstin || '';
      const clientGstinPrefix = clientGstin?.substring(0, 2) || '';
      
      if (userGstinPrefix && clientGstinPrefix && userGstinPrefix === clientGstinPrefix) {
        const cgstRate = gstRate / 2;
        const sgstRate = gstRate / 2;
        const cgstAmount = (baseAmount * cgstRate) / 100;
        const sgstAmount = (baseAmount * sgstRate) / 100;
        return { type: 'intrastate', cgstRate, sgstRate, cgstAmount, sgstAmount, totalGst: cgstAmount + sgstAmount };
      } else {
        const igstAmount = (baseAmount * gstRate) / 100;
        return { type: 'interstate', igstRate: gstRate, igstAmount, totalGst: igstAmount };
      }
    }
    return { type: 'no_gst', totalGst: 0 };
  };

  const printInvoice = () => {
    const gstBreakdown = calculateGSTBreakdown(invoice, user);
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Invoice ${invoice.invoice_number}</title>
        <style>
          @page { size: A4; margin: 20mm; }
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #333; background: white; }
          .invoice-container { max-width: 800px; margin: 0 auto; background: white; padding: 40px; }
          .invoice-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; border-bottom: 3px solid #2563eb; padding-bottom: 20px; }
          .company-info { flex: 1; }
          .company-name { font-size: 28px; font-weight: bold; color: #2563eb; margin-bottom: 5px; }
          .company-details { color: #666; font-size: 14px; }
          .invoice-title { text-align: right; flex: 1; }
          .invoice-title h1 { font-size: 36px; color: #2563eb; margin-bottom: 5px; }
          .invoice-number { font-size: 16px; color: #666; margin-bottom: 10px; }
          .invoice-dates { font-size: 14px; color: #666; }
          .billing-section { display: flex; justify-content: space-between; margin-bottom: 40px; }
          .billing-info { flex: 1; padding-right: 40px; }
          .billing-info h3 { color: #2563eb; margin-bottom: 10px; font-size: 18px; }
          .billing-details { background: #f8fafc; padding: 20px; border-radius: 8px; border-left: 4px solid #2563eb; }
          .billing-details p { margin-bottom: 5px; font-size: 14px; }
          .services-table { width: 100%; border-collapse: collapse; margin-bottom: 40px; }
          .services-table th { background: #2563eb; color: white; padding: 15px; text-align: left; }
          .services-table td { padding: 15px; border-bottom: 1px solid #e5e7eb; }
          .amount-section { text-align: right; margin-bottom: 40px; }
          .amount-row { display: flex; justify-content: flex-end; margin-bottom: 10px; }
          .amount-label { width: 200px; text-align: right; padding-right: 20px; font-weight: 500; }
          .amount-value { width: 120px; text-align: right; font-weight: 600; }
          .total-row { border-top: 2px solid #2563eb; padding-top: 10px; font-size: 18px; color: #2563eb; }
          .footer-section { margin-top: 50px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #666; font-size: 14px; }
          .payment-terms { background: #fef3c7; border: 1px solid #f59e0b; border-radius: 8px; padding: 20px; margin-bottom: 30px; }
          .payment-terms h4 { color: #92400e; margin-bottom: 10px; }
          @media print { body { font-size: 12px; -webkit-print-color-adjust: exact; print-color-adjust: exact; } .invoice-container { padding: 20px; box-shadow: none; } .no-print { display: none !important; } }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <div class="invoice-header">
            <div class="company-info">
              <div class="company-name">${user?.organization_name || 'Organization'}</div>
              <div class="company-details">
                ${user?.address ? `<p>${user.address}</p>` : ''}
                ${user?.city || user?.state || user?.pincode ? `<p>${[user?.city, user?.state].filter(Boolean).join(', ')}${user?.pincode ? ` - ${user.pincode}` : ''}</p>` : ''}
                <p>${user?.email}</p>
                ${user?.mobile ? `<p>Mobile: ${user.mobile}</p>` : ''}
                ${user?.gstin ? `<p><strong>GSTIN:</strong> ${user.gstin}</p>` : ''}
              </div>
            </div>
            <div class="invoice-title">
              <h1>${user?.gstin ? 'TAX INVOICE' : 'INVOICE'}</h1>
              <div class="invoice-number">#${invoice.invoice_number}</div>
              <div class="invoice-dates">
                <div><strong>Date:</strong> ${new Date(invoice.invoice_date).toLocaleDateString('en-IN')}</div>
                <div><strong>Place of Supply:</strong> ${user?.state || 'Not Specified'}</div>
              </div>
            </div>
          </div>
          <div class="billing-section">
            <div class="billing-info">
              <h3>Bill To:</h3>
              <div class="billing-details">
                <p><strong>${invoice.client_name || invoice.client?.name || 'N/A'}</strong></p>
                ${invoice.organization_name ? `<p>${invoice.organization_name}</p>` : ''}
                ${invoice.client?.address ? `<p>${invoice.client.address}</p>` : ''}
                ${invoice.client_email ? `<p>Email: ${invoice.client_email}</p>` : ''}
                ${invoice.client_mobile ? `<p>Mobile: ${invoice.client_mobile}</p>` : ''}
                ${invoice.client_gstin ? `<p><strong>GSTIN:</strong> ${invoice.client_gstin}</p>` : ''}
              </div>
            </div>
          </div>
          <table class="services-table">
            <thead><tr><th>Service</th><th style="text-align:right">Amount</th><th style="text-align:right">GST</th><th style="text-align:right">Total</th></tr></thead>
            <tbody><tr>
              <td>${invoice.service_name || invoice.service?.name || 'Service'}</td>
              <td style="text-align:right">Rs.${(invoice.base_amount || invoice.amount || 0).toLocaleString('en-IN')}</td>
              <td style="text-align:right">Rs.${(invoice.gst_amount || 0).toLocaleString('en-IN')}</td>
              <td style="text-align:right"><strong>Rs.${(invoice.total_amount || 0).toLocaleString('en-IN')}</strong></td>
            </tr></tbody>
          </table>
          <div class="amount-section">
            <div class="amount-row"><div class="amount-label">Subtotal:</div><div class="amount-value">Rs.${(invoice.base_amount || invoice.amount || 0).toLocaleString('en-IN')}</div></div>
            ${gstBreakdown.type === 'intrastate' ? `
            <div class="amount-row"><div class="amount-label">CGST (${gstBreakdown.cgstRate}%):</div><div class="amount-value">Rs.${gstBreakdown.cgstAmount.toLocaleString('en-IN')}</div></div>
            <div class="amount-row"><div class="amount-label">SGST (${gstBreakdown.sgstRate}%):</div><div class="amount-value">Rs.${gstBreakdown.sgstAmount.toLocaleString('en-IN')}</div></div>
            ` : ''}
            ${gstBreakdown.type === 'interstate' ? `
            <div class="amount-row"><div class="amount-label">IGST (${gstBreakdown.igstRate}%):</div><div class="amount-value">Rs.${gstBreakdown.igstAmount.toLocaleString('en-IN')}</div></div>
            ` : ''}
            ${gstBreakdown.type === 'no_gst' ? `
            <div class="amount-row"><div class="amount-label">Tax:</div><div class="amount-value">Rs.0 (No GST)</div></div>
            ` : ''}
            <div class="amount-row total-row"><div class="amount-label">Total Amount:</div><div class="amount-value">Rs.${((invoice.base_amount || invoice.amount || 0) + gstBreakdown.totalGst).toLocaleString('en-IN')}</div></div>
          </div>
          <div class="payment-terms"><h4>Payment Terms:</h4><p>Payment due within 30 days. Contact: ${user?.email}</p></div>
          <div class="footer-section"><p><strong>Thank you for your business!</strong></p><p>Computer-generated invoice.</p></div>
        </div>
      </body>
      </html>
    `;
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 1000);
  };

  const generateInvoiceText = () => {
    return `INVOICE
Invoice #: ${invoice.invoice_number}
Date: ${new Date(invoice.invoice_date).toLocaleDateString('en-IN')}
Due Date: ${new Date(invoice.due_date).toLocaleDateString('en-IN')}

From:
${user?.organization_name || 'Organization'}
${user?.email}
${user?.gstin ? `GSTIN: ${user.gstin}` : ''}

Bill To:
${invoice.client_name || invoice.client?.name || 'N/A'}
${invoice.client_email || ''}
${invoice.client_gstin ? `GSTIN: ${invoice.client_gstin}` : ''}

Service: ${invoice.service_name || invoice.service?.name || 'Service'}
Amount: Rs.${(invoice.base_amount || invoice.amount || 0).toLocaleString('en-IN')}
GST: Rs.${(invoice.gst_amount || 0).toLocaleString('en-IN')}
Total: Rs.${(invoice.total_amount || 0).toLocaleString('en-IN')}

Status: ${invoice.status?.toUpperCase()}

Payment due within 30 days.`;
  };

  const sendViaWhatsApp = async () => {
    try {
      setIsSharing(true);
      const invoiceText = generateInvoiceText();
      const clientMobile = invoice.client_mobile || invoice.client?.mobile || '';
      if (window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        const whatsappUrl = `https://wa.me/${clientMobile ? clientMobile.replace(/[^\d]/g, '') : ''}?text=${encodeURIComponent(invoiceText)}`;
        window.open(whatsappUrl, '_blank');
      } else {
        const response = await axios.post(`${API}/whatsapp/web/send`, { phone_number: clientMobile, message: invoiceText });
        if (response.data.success) { alert('Invoice sent via WhatsApp!'); }
        else { throw new Error('Failed'); }
      }
    } catch (error) {
      console.error('Error sending via WhatsApp:', error);
      alert('Error sending invoice via WhatsApp');
    } finally {
      setIsSharing(false);
    }
  };

  const sendPDFViaWhatsApp = async () => {
    try {
      setIsSharing(true);
      const clientMobile = invoice.client_mobile || invoice.client?.mobile || '';
      if (!clientMobile) { alert('Client mobile number not available'); return; }
      const response = await axios.post(`${API}/invoices/${invoice.id}/send-pdf-whatsapp`, null, { params: { phone_number: clientMobile } });
      if (response.data.success) { alert('Invoice PDF sent via WhatsApp!'); }
      else { throw new Error('Failed to send PDF'); }
    } catch (error) {
      console.error('Error sending PDF via WhatsApp:', error);
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSharing(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${invoice.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error downloading PDF: ' + (error.response?.data?.detail || error.message));
    }
  };

  const sendViaEmail = () => {
    const subject = `Invoice ${invoice.invoice_number} - ${invoice.client_name || invoice.client?.name || ''}`;
    const body = generateInvoiceText();
    window.location.href = `mailto:${invoice.client_email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const sendViaTelegram = () => {
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(generateInvoiceText())}`;
    window.open(telegramUrl, '_blank');
  };

  const shareInvoice = async () => {
    const invoiceText = generateInvoiceText();
    if (navigator.share) {
      try { await navigator.share({ title: `Invoice ${invoice.invoice_number}`, text: invoiceText, url: window.location.href }); }
      catch (error) { if (error.name !== 'AbortError') console.error('Error sharing:', error); }
    } else {
      try { await navigator.clipboard.writeText(invoiceText); alert('Invoice details copied to clipboard!'); }
      catch (error) { alert('Unable to copy.'); }
    }
  };

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) { alert('Please select a valid image file'); return; }
      if (file.size > 5 * 1024 * 1024) { alert('Image size should be less than 5MB'); return; }
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => { setImagePreview(e.target.result); };
      reader.readAsDataURL(file);
    }
  };

  const sendImageViaWhatsApp = async () => {
    if (!imageFile) { alert('Please select an image first'); return; }
    const clientMobile = invoice.client_mobile || invoice.client?.mobile || '';
    if (!clientMobile) { alert('Client mobile number not found'); return; }
    try {
      setIsSendingImage(true);
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64Data = e.target.result.split(',')[1];
          const response = await axios.post(`${API}/whatsapp/web/send-image`, {
            phone_number: clientMobile,
            message: `Invoice ${invoice.invoice_number} from ${user?.organization_name || user?.name}. Amount: Rs.${(invoice.total_amount || 0).toLocaleString()}`,
            image_data: base64Data,
            filename: `invoice_${invoice.invoice_number}_${imageFile.name}`
          });
          if (response.data.success) {
            alert('Image sent via WhatsApp!');
            setShowWhatsappImageModal(false);
            setImageFile(null);
            setImagePreview(null);
          } else { throw new Error(response.data.error || 'Failed'); }
        } catch (error) { alert('Failed to send image.'); }
        finally { setIsSendingImage(false); }
      };
      reader.readAsDataURL(imageFile);
    } catch (error) { alert('Failed to process image.'); setIsSendingImage(false); }
  };

  return (
    <div className="min-h-screen bg-white print:bg-white">
      <div className="max-w-4xl mx-auto p-8">
        {/* Header */}
        <div className="flex justify-between items-start mb-8 print:mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">{user?.gstin ? 'TAX INVOICE' : 'INVOICE'}</h1>
            <p className="text-gray-600">#{invoice.invoice_number}</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-purple-600">{user?.organization_name || 'Organization'}</div>
            <div className="text-sm text-gray-600">
              {user?.address && <div>{user.address}</div>}
              {(user?.city || user?.state || user?.pincode) && (
                <div>{[user?.city, user?.state].filter(Boolean).join(', ')}{user?.pincode && ` - ${user.pincode}`}</div>
              )}
              <div>{user?.email}</div>
              {user?.mobile && <div>Mobile: {user.mobile}</div>}
              {user?.gstin && <div><strong>GSTIN:</strong> {user.gstin}</div>}
            </div>
          </div>
        </div>

        {/* Invoice Details */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Bill To:</h3>
            <div className="text-gray-700">
              <div className="font-medium">{invoice.client_name || invoice.client?.name || 'N/A'}</div>
              {invoice.organization_name && <div>{invoice.organization_name}</div>}
              {invoice.client?.address && <div>{invoice.client.address}</div>}
              {invoice.client_email && <div>Email: {invoice.client_email}</div>}
              {invoice.client_mobile && <div>Mobile: {invoice.client_mobile}</div>}
              {invoice.client_gstin && <div className="mt-1"><span className="font-medium">GSTIN:</span> {invoice.client_gstin}</div>}
            </div>
          </div>
          <div className="text-right">
            <div className="space-y-2">
              <div><span className="font-medium text-gray-600">Invoice Date:</span><div>{new Date(invoice.invoice_date).toLocaleDateString()}</div></div>
              <div><span className="font-medium text-gray-600">Place of Supply:</span><div>{user?.state || 'Not Specified'}</div></div>
              <div>
                <span className="font-medium text-gray-600">Status:</span>
                <Badge variant={invoice.status === 'paid' ? 'default' : invoice.status === 'overdue' ? 'destructive' : 'secondary'}>
                  {(invoice.status || 'draft').toUpperCase()}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Items */}
        <div className="mb-8">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Service</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right">Quantity</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>{invoice.service_name || invoice.service?.name || 'Service'}</TableCell>
                <TableCell>{invoice.service_description || invoice.service?.description || '-'}</TableCell>
                <TableCell className="text-right">{invoice.rate || invoice.amount || 0}</TableCell>
                <TableCell className="text-right">{invoice.quantity || 1}</TableCell>
                <TableCell className="text-right">{invoice.amount || 0}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {/* Invoice Totals */}
        <div className="flex justify-end mb-8">
          <div className="w-80 space-y-2">
            <div className="flex justify-between"><span>Subtotal:</span><span>{invoice.base_amount || invoice.amount || 0}</span></div>
            {(() => {
              const gstBreakdown = calculateGSTBreakdown(invoice, user);
              if (gstBreakdown.type === 'intrastate') {
                return (<>
                  <div className="flex justify-between"><span>CGST ({gstBreakdown.cgstRate}%):</span><span>{gstBreakdown.cgstAmount.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span>SGST ({gstBreakdown.sgstRate}%):</span><span>{gstBreakdown.sgstAmount.toFixed(2)}</span></div>
                </>);
              } else if (gstBreakdown.type === 'interstate') {
                return (<div className="flex justify-between"><span>IGST ({gstBreakdown.igstRate}%):</span><span>{gstBreakdown.igstAmount.toFixed(2)}</span></div>);
              } else {
                return (<div className="flex justify-between"><span>Tax:</span><span>0 (No GST)</span></div>);
              }
            })()}
            <hr />
            <div className="flex justify-between text-lg font-bold"><span>Total:</span><span>{((invoice.base_amount || invoice.amount || 0) + calculateGSTBreakdown(invoice, user).totalGst).toFixed(2)}</span></div>
          </div>
        </div>

        {invoice.notes && (
          <div className="mb-8"><h3 className="text-lg font-semibold text-gray-800 mb-2">Notes:</h3><p className="text-gray-700">{invoice.notes}</p></div>
        )}

        {/* Actions */}
        <div className="print:hidden space-y-4">
          <div className="flex justify-between items-center">
            <Button variant="outline" onClick={onClose} data-testid="close-invoice-view"><Eye className="w-4 h-4 mr-2" />Close</Button>
            <div className="space-x-2">
              <Button onClick={printInvoice} variant="outline" data-testid="print-invoice"><FileText className="w-4 h-4 mr-2" />Print</Button>
              <Button onClick={downloadPDF} variant="outline" data-testid="download-pdf-modal"><Download className="w-4 h-4 mr-2" />Download PDF</Button>
            </div>
          </div>

          {/* Sharing Options */}
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-lg text-blue-800 flex items-center"><Send className="w-5 h-5 mr-2" />Share Invoice</CardTitle>
              <CardDescription className="text-blue-600">Send this invoice to your client</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <Button onClick={sendPDFViaWhatsApp} disabled={isSharing} className="bg-green-600 hover:bg-green-700 text-white flex items-center justify-center">
                  <MessageCircle className="w-4 h-4 mr-2" />{isSharing ? 'Sending...' : 'WhatsApp PDF'}
                </Button>
                <Button onClick={sendViaWhatsApp} disabled={isSharing} variant="outline" className="border-green-600 text-green-600 hover:bg-green-50 flex items-center justify-center">
                  <MessageCircle className="w-4 h-4 mr-2" />Text Only
                </Button>
                <Button onClick={() => { const m = invoice.client_mobile || invoice.client?.mobile; if (!m) { alert('No mobile number'); return; } setShowWhatsappImageModal(true); }} variant="outline" className="border-purple-600 text-purple-600 hover:bg-purple-50 flex items-center justify-center">
                  <Upload className="w-4 h-4 mr-2" />With Image
                </Button>
                <Button onClick={sendViaTelegram} className="bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center">
                  <Send className="w-4 h-4 mr-2" />Telegram
                </Button>
                <Button onClick={sendViaEmail} className="bg-gray-600 hover:bg-gray-700 text-white flex items-center justify-center">
                  <Mail className="w-4 h-4 mr-2" />Email
                </Button>
                <Button onClick={shareInvoice} className="bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center">
                  <Share className="w-4 h-4 mr-2" />More
                </Button>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start">
                  <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 mr-2" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">PDF Sharing Options:</p>
                    <ul className="text-blue-700 mt-1 space-y-1">
                      <li>WhatsApp PDF: Professional PDF with logo & signature</li>
                      <li>Download PDF: Save to device for manual sharing</li>
                      <li>Print: Paper copies via print dialog</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* WhatsApp Image Upload Modal */}
      {showWhatsappImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Send Image via WhatsApp</h3>
                <button onClick={() => { setShowWhatsappImageModal(false); setImageFile(null); setImagePreview(null); }} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="space-y-4">
                <div className="text-sm text-gray-600">
                  <p><strong>To:</strong> {invoice.client_name || invoice.client?.name}</p>
                  <p><strong>Mobile:</strong> {invoice.client_mobile || invoice.client?.mobile}</p>
                  <p><strong>Invoice:</strong> {invoice.invoice_number}</p>
                </div>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  {imagePreview ? (
                    <div className="space-y-3">
                      <img src={imagePreview} alt="Selected" className="max-w-full max-h-48 mx-auto rounded" />
                      <div className="text-sm text-gray-600">{imageFile?.name} ({(imageFile?.size / 1024 / 1024).toFixed(2)} MB)</div>
                      <button onClick={() => document.getElementById('whatsapp-image-input-view').click()} className="text-blue-600 hover:text-blue-800 underline">Choose different image</button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                      <div><p className="text-gray-600 mb-2">Select an image</p><button onClick={() => document.getElementById('whatsapp-image-input-view').click()} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">Select Image</button></div>
                      <p className="text-xs text-gray-500">Max: 5MB. JPG, PNG, GIF</p>
                    </div>
                  )}
                  <input id="whatsapp-image-input-view" type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
                </div>
                <div className="flex space-x-3 pt-4">
                  <Button onClick={() => { setShowWhatsappImageModal(false); setImageFile(null); setImagePreview(null); }} variant="outline" className="flex-1" disabled={isSendingImage}>Cancel</Button>
                  <Button onClick={sendImageViaWhatsApp} className="flex-1 bg-green-600 hover:bg-green-700" disabled={!imageFile || isSendingImage}>
                    {isSendingImage ? (<div className="flex items-center"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Sending...</div>) : (<><MessageCircle className="w-4 h-4 mr-2" />Send via WhatsApp</>)}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewInvoice;
